import apiClient from './client';

export interface User {
  id: string;
  email: string | null;
  username: string;
  firstName: string | null;
  lastName: string | null;
  role: 'user' | 'admin';
  is_anonymous: boolean;
  isSubscribed: boolean;
  subscribedEnd: Date | null;
  lastLogin: Date | null;
  created_at: Date;
  updated_at: Date;
  mock_exam_attempts_used: number;
  wrong_exam_attempts_used: number;
  marathon_attempts_used: number;
  goal: 'first_exam' | 'retake' | 'repeat' | null;
  deadline: '3_days' | '1_week' | '1_month' | null;
  deviceId: string | null;
  googleId: string | null;
  appleId: string | null;
}

export interface GetUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: 'user' | 'admin';
  isSubscribed?: string;
}

export interface UsersListResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface UpdateUserDto {
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  role?: 'user' | 'admin';
  isSubscribed?: boolean;
}

export interface UserLimits {
  mock_exam_attempts_used: number;
  wrong_exam_attempts_used: number;
  marathon_attempts_used: number;
  mock_exam_attempts_remaining: number;
  wrong_exam_attempts_remaining: number;
  marathon_attempts_remaining: number;
  isPremium: boolean;
}

export interface UserDetails extends User {
  limits: UserLimits;
}

export interface UsersStats {
  total: number;
  activeUsers: {
    day: number;
    week: number;
    month: number;
  };
}

export interface DailyActiveUsersData {
  day: string;
  registrations: number;
  successfulPayments: number;
}

export interface ActiveUsersComparison {
  name: string;
  current: number;
  previous: number;
}

export interface DashboardStats {
  totalUsers: number;
  subscribedUsers: number;
  totalRevenue: number;
  dailyActiveUsers: DailyActiveUsersData[];
  activeUsersByPeriod: ActiveUsersComparison[];
}

export const usersApi = {
  getUsers: async (params: GetUsersParams = {}): Promise<UsersListResponse> => {
    const response = await apiClient.get<UsersListResponse>('/users', { params });
    return response.data;
  },

  getUserById: async (id: string): Promise<User> => {
    const response = await apiClient.get<User>(`/users/${id}`);
    return response.data;
  },

  updateUser: async (id: string, dto: UpdateUserDto): Promise<User> => {
    const response = await apiClient.patch<User>(`/users/${id}`, dto);
    return response.data;
  },

  /**
   * Вручную выдать премиум до указанной даты. `subscribedEnd` —
   * ISO 8601 строка (например, `new Date(...).toISOString()`).
   */
  grantPremium: async (id: string, subscribedEnd: string): Promise<User> => {
    const response = await apiClient.post<User>(`/users/${id}/grant-premium`, {
      subscribedEnd,
    });
    return response.data;
  },

  /** Снять премиум: isSubscribed=false, subscribedEnd=null. */
  revokePremium: async (id: string): Promise<User> => {
    const response = await apiClient.post<User>(`/users/${id}/revoke-premium`);
    return response.data;
  },

  getUserDetails: async (id: string): Promise<UserDetails> => {
    const response = await apiClient.get<UserDetails>(`/users/${id}/details`);
    return response.data;
  },

  getUsersStats: async (): Promise<UsersStats> => {
    const response = await apiClient.get<UsersStats>('/users/stats');
    return response.data;
  },

  getDashboardStats: async (): Promise<DashboardStats> => {
    const response = await apiClient.get<DashboardStats>('/users/dashboard-stats');
    return response.data;
  },
};

